import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { parse } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import { useTheme, useMediaQuery, Box } from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import { fetchLegMembersByFlt } from 'state/api';
import { hasPermission } from 'state/checkPermission';
import CrewListDialog from 'components/CrewListDialog';
import { processData } from './processData';
import { configureGantt } from './configureGantt';


const getElements = (str, n) => {
    if (!str || typeof str !== 'string') return [];
    return str.split(',').map(s => s.trim()).filter(Boolean).slice(0, n);
};

const formatElements = (elements) => elements.join(", ");

const drawArrowhead = (ctx, x, y) => {
    ctx.save();
    ctx.setLineDash([]);
    ctx.fillStyle = "#875f9dff";
    ctx.beginPath();
    ctx.translate(x, y);
    ctx.moveTo(0, 0);
    ctx.lineTo(-5, -2.5);
    ctx.lineTo(-5, 2.5);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
};


const FlightGanttChart = ({
    rowHeight = 50,
    cellWidth = 20,
    isShownCrewRoutes = false,
    flights = [],
}) => {
    const gantt = useMemo(() => Gantt.getGanttInstance(), []);

    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const TIMEZONE = useMemo(
        () => params?.find((item) => item.param === "TIMEZONE")?.value || 'Asia/Ho_Chi_Minh',
        [params]
    );
    
    const canView = useMemo(
        () => hasPermission(user?.permissions, "FlightGanttChart", "view", false),
        [user?.permissions]
    );

    const { startDate, endDate } = useDateRangeContext();

    const ganttContainer = useRef(null);
    const customTooltipRef = useRef(null);
    const splitterWidthRef = useRef(90);

    // Create tooltip DOM node once; update theme colors when theme changes
    useEffect(() => {
        const tooltip = document.createElement("div");
        tooltip.className = "custom-subtask-tooltip";
        tooltip.style.position = "absolute";
        tooltip.style.display = "none";
        tooltip.style.zIndex = 99;
        tooltip.style.padding = "4px 8px";
        tooltip.style.borderRadius = "4px";
        tooltip.style.fontSize = "12px";
        tooltip.style.pointerEvents = "none";
        document.body.appendChild(tooltip);
        customTooltipRef.current = tooltip;

        const hideTooltip = (e) => {
            if (!e.target.closest(".subtask")) {
                tooltip.style.display = "none";
            }
        };
        document.addEventListener("mousemove", hideTooltip);

        return () => {
            document.removeEventListener("mousemove", hideTooltip);
            tooltip.remove();
            customTooltipRef.current = null;
        };
    }, []);

    // Sync tooltip colors when theme changes
    useEffect(() => {
        const tooltip = customTooltipRef.current;
        if (!tooltip) return;
        tooltip.style.background = theme.palette.neutral.mid;
        tooltip.style.color = theme.palette.text.primary;
    }, [theme.palette.neutral.mid, theme.palette.text.primary]);

    const [dialogState, setDialogState] = useState({
        open: false,
        title: 'CREW LIST',
        flight: [],
        crewList: [],
        jumpList: [],
    });

    const handleOpenCrewListDialog = useCallback(async (flightDate, flightDesc, dep) => {
        try {
            const { flightDetails, legMembers, jumpSeaters } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);
            setDialogState({
                open: true,
                title: canView ? 'CREW LIST' : 'FLIGHT',
                flight: flightDetails,
                crewList: canView ? legMembers : [],
                jumpList: canView ? jumpSeaters : [],
            });
        } catch {
            setDialogState(prev => ({ ...prev, open: false }));
        }
    }, [canView]);

    const handleCloseCrewListDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, open: false }));
    }, []);

    const showSubtasks = useCallback((task) => {
        if (!gantt.hasChild(task.id)) return false;

        const sub_tasks = gantt.getChildren(task.id);
        const subtasks = [];

        for (let i = 0; i < sub_tasks.length; i++) {
            const child = gantt.getTask(sub_tasks[i]);
            if (child.text === task.text) continue;

            const child_sizes = gantt.getTaskPosition(child);
            const left_position = child_sizes.left - gantt.getTaskPosition(task).left;
            const task_width = child_sizes.width;

            const safe = (val) => (val == null ? "" : val);
            subtasks.push(`
                <span
                    class='subtask'
                    data-id='${child.id}'
                    data-flightDate='${safe(child.flightDate)}'
                    data-flightDateLocalBase='${safe(child.flightDateLocalBase)}'
                    data-flightDesc='${safe(child.flightDesc)}'
                    data-dep='${safe(child.dep)}'
                    data-arr='${safe(child.arr)}'
                    data-sector='${safe(child.sector)}'
                    data-sectorType='${safe(child.sectorType)}'
                    data-etd='${safe(child.etd)}'
                    data-eta='${safe(child.eta)}'
                    data-atd='${safe(child.atd)}'
                    data-ata='${safe(child.ata)}'
                    data-etdLocalBase='${safe(child.etdLocalBase)}'
                    data-etaLocalBase='${safe(child.etaLocalBase)}'
                    data-atdLocalBase='${safe(child.atdLocalBase)}'
                    data-ataLocalBase='${safe(child.ataLocalBase)}'
                    data-expectedPaxPerCategory='${safe(child.expectedPaxPerCategory)}'
                    data-paxPerCategory='${safe(child.paxPerCategory)}'
                    data-punctuality='${child.punctuality}'
                    style='position: absolute; left: ${left_position}px; width: ${task_width}px;'
                >
                    ${child.text}
                </span>`);
        }

        setTimeout(() => {
            document.querySelectorAll('.subtask').forEach(span => {
                const flightDate = span.getAttribute('data-flightDate');
                const flightDesc = span.getAttribute("data-flightDesc") || "";
                const dep = span.getAttribute('data-dep');
                const sector = span.getAttribute("data-sector") || "";
                const etd = span.getAttribute("data-etd") || "";
                const eta = span.getAttribute("data-eta") || "";
                const atd = span.getAttribute("data-atd") || etd;
                const ata = span.getAttribute("data-ata") || eta;
                const etdLocalBase = span.getAttribute("data-etdLocalBase") || "";
                const etaLocalBase = span.getAttribute("data-etaLocalBase") || "";
                const atdLocalBase = span.getAttribute("data-atdLocalBase") || etdLocalBase;
                const ataLocalBase = span.getAttribute("data-ataLocalBase") || etaLocalBase;
                const expectedPaxPerCategory = span.getAttribute("data-expectedPaxPerCategory") || "";
                const paxPerCategory = span.getAttribute("data-paxPerCategory") || "";

                if (!flightDesc) return;

                span.onclick = (e) => {
                    e.stopPropagation();
                    document.querySelectorAll('.subtask').forEach(s => s.classList.remove('selected-blinking'));
                    span.classList.add('selected-blinking');
                    if (customTooltipRef.current) customTooltipRef.current.style.display = "none";
                    if (flightDate && flightDesc && dep) {
                        handleOpenCrewListDialog(flightDate, flightDesc, dep);
                    }
                };

                if (!isMobile) {
                    span.onmouseenter = (e) => {
                        const tooltip = customTooltipRef.current;
                        if (!tooltip) return;
                        const expectedText = expectedPaxPerCategory
                            ? `Expected: ${formatElements(getElements(expectedPaxPerCategory, 2))}`
                            : '';
                        const actualText = paxPerCategory
                            ? `Actual: ${formatElements(getElements(paxPerCategory, 2))}`
                            : '';
                        tooltip.innerHTML = `
                            ${flightDesc} ${timeMode === 'UTC' ? atd : atdLocalBase} ${sector} ${timeMode === 'UTC' ? ata : ataLocalBase}
                            ${expectedText ? `<br/>${expectedText}` : ''}
                            ${actualText ? `<br/>${actualText}` : ''}
                        `;
                        tooltip.style.left = e.pageX + 10 + "px";
                        tooltip.style.top = e.pageY + 10 + "px";
                        tooltip.style.display = "block";
                    };

                    span.onmousemove = (e) => {
                        const tooltip = customTooltipRef.current;
                        if (!tooltip) return;
                        tooltip.style.left = e.pageX + 10 + "px";
                        tooltip.style.top = e.pageY + 10 + "px";
                    };

                    span.onmouseleave = () => {
                        if (customTooltipRef.current) customTooltipRef.current.style.display = "none";
                    };

                    span.ontouchstart = () => {
                        if (customTooltipRef.current) customTooltipRef.current.style.display = "none";
                    };
                }
            });
        }, 0);

        return subtasks.join("");
    }, [gantt, timeMode, isMobile, handleOpenCrewListDialog]);

    const showSubtasksRef = useRef(showSubtasks);
    useEffect(() => {
        showSubtasksRef.current = showSubtasks;
    }, [showSubtasks]);

    const getNow = useCallback(() => {
        return timeMode === 'UTC'
            ? toZonedTime(new Date(), 'UTC')
            : toZonedTime(new Date(), TIMEZONE);
    }, [timeMode, TIMEZONE]);

    const drawNowLine = useCallback(() => {
        const container = ganttContainer.current?.querySelector(".gantt_task_bg");
        if (!container) return;

        const now = getNow();
        const state = gantt.getState();
        const isWithinRange = now >= state.min_date && now <= state.max_date;

        let nowLine = container.querySelector(".gantt_now_line");
        if (!isWithinRange) {
            nowLine?.remove();
            return;
        }

        if (!nowLine) {
            nowLine = document.createElement("div");
            nowLine.className = "gantt_now_line";
            container.appendChild(nowLine);
        }

        const position = gantt.posFromDate(now);
        Object.assign(nowLine.style, {
            zIndex: "9",
            left: `${position}px`,
            top: "0px",
            width: "2px",
            height: `${container.scrollHeight}px`,
            position: "absolute",
            backgroundColor: "red",
            pointerEvents: "none",
        });
    }, [gantt, getNow]);

    const drawWeekendColumns = useCallback(() => {
        const bgContainer = ganttContainer.current?.querySelector(".gantt_task_bg");
        if (!bgContainer) return;

        bgContainer.querySelectorAll(".weekend-column-layer").forEach(el => el.remove());

        const state = gantt.getState();
        const start = startDate ? new Date(new Date(startDate).setHours(0, 0, 0, 0)) : new Date(state.min_date.setHours(0, 0, 0, 0));
        const end = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)) : new Date(state.max_date.setHours(23, 59, 59, 999));

        let date = new Date(start);
        while (date <= end) {
            const day = date.getDay();
            if (day === 6 || day === 0) {
                const left = gantt.posFromDate(date);
                const right = gantt.posFromDate(gantt.date.add(date, 1, "day"));

                const div = document.createElement("div");
                div.className = "weekend-column-layer " + (day === 6 ? "weekend-saturday" : "weekend-sunday");
                Object.assign(div.style, {
                    top: "0px",
                    left: `${left}px`,
                    width: `${right - left}px`,
                    height: `${bgContainer.scrollHeight}px`,
                });
                bgContainer.appendChild(div);
            }
            date = gantt.date.add(date, 1, "day");
        }
    }, [gantt, startDate, endDate]);

    const routeMap = useMemo(() => {
        const map = new Map();
        flights.forEach(flight => {
            flight.crewRoutes?.forEach(route => {
                if (!route.routeNo || !route.routeDate) return;
                const routeDate = parse(route.routeDate, "dd/MM/yyyy", new Date()).toISOString().split("T")[0];
                const routeKey = `${routeDate}_${route.routeNo}`;
                if (!map.has(routeKey)) map.set(routeKey, []);
                map.get(routeKey).push(flight);
            });
        });
        return map;
    }, [flights]);

    const drawRouteLinks = useCallback(() => {
        const ganttEl = ganttContainer.current;
        if (!ganttEl) return;

        const taskArea = ganttEl.querySelector(".gantt_task");
        if (!taskArea) return;

        taskArea.querySelector(".gantt_custom_links_canvas")?.remove();

        const canvas = document.createElement("canvas");
        canvas.className = "gantt_custom_links_canvas";

        const width = taskArea.scrollWidth;
        const height = taskArea.scrollHeight;
        const dpr = window.devicePixelRatio || 1;

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        Object.assign(canvas.style, {
            width: `${width}px`,
            height: `${height}px`,
            position: "absolute",
            top: "0",
            left: "0",
            zIndex: "1",
            pointerEvents: "none",
        });
        taskArea.appendChild(canvas);

        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);

        const blockMap = new Map();
        taskArea.querySelectorAll(".flight-multi-line-text").forEach(b => {
            const id = b.getAttribute("data-id");
            if (id) blockMap.set(id, b);
        });

        ctx.strokeStyle = "#875f9dff";
        ctx.lineWidth = 1.2;
        const taskRect = taskArea.getBoundingClientRect();

        for (const flightGroup of routeMap.values()) {
            if (flightGroup.length < 2) continue;

            const sorted = [...flightGroup].sort(
                (a, b) => new Date(a.fullAtd || a.fullEtd) - new Date(b.fullAtd || b.fullEtd)
            );

            for (let i = 0; i < sorted.length - 1; i++) {
                const source = blockMap.get(sorted[i]._id);
                const target = blockMap.get(sorted[i + 1]._id);
                if (!source || !target) continue;

                const srcRect = source.getBoundingClientRect();
                const tgtRect = target.getBoundingClientRect();

                const x1 = srcRect.right - taskRect.left + taskArea.scrollLeft;
                const y1 = srcRect.top + srcRect.height / 2 - taskRect.top + taskArea.scrollTop;
                const x2 = tgtRect.left - taskRect.left + taskArea.scrollLeft;
                const y2 = tgtRect.top + tgtRect.height / 2 - taskRect.top + taskArea.scrollTop;

                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.bezierCurveTo(x1 + 20, y1, x2 - 20, y2, x2, y2);
                ctx.stroke();

                drawArrowhead(ctx, x2, y2);
            }
        }
    }, [routeMap]);

    useEffect(() => {
        const { tasks, links } = processData(flights, timeMode, cellWidth);

        configureGantt(gantt, rowHeight, cellWidth, splitterWidthRef.current, showSubtasksRef);

        const ganttEl = ganttContainer.current;
        if (!ganttEl) return;

        gantt.init(ganttEl);
        gantt.parse({ tasks, links });
        gantt.render();

        const now = getNow();
        const ganttState = gantt.getState();
        if (now >= ganttState.min_date && now <= ganttState.max_date) {
            gantt.scrollTo(gantt.posFromDate(now) - gantt.config.task_scroll_offset, 0);
        }

        drawWeekendColumns();
        drawNowLine();
        if (isShownCrewRoutes) drawRouteLinks();

        const scrollEventId = gantt.attachEvent("onGanttScroll", () => {
            drawNowLine();
            drawWeekendColumns();
            if (isShownCrewRoutes) drawRouteLinks();
        });
        gantt.attachEvent("onGridResizeEnd", (_oldWidth, newWidth) => {
            gantt.config.columns[0].width = newWidth;
            return true;
        });
        window.addEventListener("resize", drawNowLine);
        const intervalId = setInterval(drawNowLine, 60000);

        return () => {
            gantt.clearAll();
            gantt.detachEvent(scrollEventId);
            window.removeEventListener("resize", drawNowLine);
            clearInterval(intervalId);
            ganttEl.querySelector(".gantt_task_bg")?.querySelector(".gantt_now_line")?.remove();
        };
    }, [
        gantt,
        timeMode,
        rowHeight,
        cellWidth,
        flights,
        getNow,
        drawNowLine,
        drawWeekendColumns,
        drawRouteLinks,
        isShownCrewRoutes,
    ]);

    return (
        <Box>
            <div
                ref={ganttContainer}
                className="flight-gantt-container"
                style={{
                    width: '100%',
                    height: LAYOUT_HEIGHT,
                    '--flight-row-height': `${rowHeight}`,
                    '--scale-height': '60px',
                }}
            />

            {dialogState.open &&
                <CrewListDialog
                    open={dialogState.open}
                    onClose={handleCloseCrewListDialog}
                    title={dialogState.title}
                    flight={dialogState.flight}
                    crewList={dialogState.crewList}
                    jumpList={dialogState.jumpList}
                />
            }
        </Box>
    );
};

export default FlightGanttChart;
